// Constants for Minew Testing Tool

import type { TestBeacon, RoomType } from './types';

// Beacon types for different entity types
export type BeaconType = 'patient' | 'staff' | 'physician' | 'anesthesiologist';

export interface TestBeaconExtended extends TestBeacon {
  type: BeaconType;
  entityName: string;
}

// Test beacon IDs (matching SeedData.scala patterns - AC233F is valid Minew prefix)
export const TEST_BEACONS: TestBeaconExtended[] = [
  {
    id: 'AC233F000001',
    label: 'Sarah Mitchell',
    color: '#4CAF50',
    type: 'patient',
    entityName: 'Sarah Mitchell',
  },
  {
    id: 'AC233F000002',
    label: 'James Rodriguez',
    color: '#2196F3',
    type: 'patient',
    entityName: 'James Rodriguez',
  },
  { id: 'AC233F000003', label: 'Emily Chen', color: '#FF9800', type: 'patient', entityName: 'Emily Chen' },
  {
    id: 'AC233F000004',
    label: 'Michael Thompson',
    color: '#9C27B0',
    type: 'patient',
    entityName: 'Michael Thompson',
  },
  {
    id: 'AC233F000005',
    label: 'Jennifer Williams',
    color: '#E91E63',
    type: 'patient',
    entityName: 'Jennifer Williams',
  },
  {
    id: 'AC233F000101',
    label: 'Nurse Patricia',
    color: '#00BCD4',
    type: 'staff',
    entityName: 'Patricia Williams',
  },
  { id: 'AC233F000102', label: 'Nurse Maria', color: '#009688', type: 'staff', entityName: 'Maria Rodriguez' },
  {
    id: 'AC233F000201',
    label: 'Dr. Anderson',
    color: '#3F51B5',
    type: 'physician',
    entityName: 'Dr. Robert Anderson',
  },
  {
    id: 'AC233F000202',
    label: 'Dr. Chen',
    color: '#673AB7',
    type: 'physician',
    entityName: 'Dr. David Chen',
  },
  {
    id: 'AC233F000301',
    label: 'Dr. Miller (Anesth)',
    color: '#795548',
    type: 'anesthesiologist',
    entityName: 'Dr. Richard Miller',
  },
];

// Room type to icon mapping
export const ROOM_TYPE_ICONS: Record<RoomType, string> = {
  WaitingRoom: 'event_seat',
  PreOp: 'assignment',
  OperatingRoom: 'local_hospital',
  PACU: 'hotel',
  PostOp: 'airline_seat_flat',
  Utility: 'build',
  Virtual: 'cloud',
};

// Room type display order for patient flow
export const ROOM_TYPE_ORDER: RoomType[] = ['WaitingRoom', 'PreOp', 'OperatingRoom', 'PACU', 'PostOp'];

// Default simulation settings
export const DEFAULT_SIGNAL_STRENGTH = -50;
// Real Minew sensors broadcast every 5-10 seconds. Using 5000ms as default.
export const DEFAULT_PING_INTERVAL = 5000;
export const DEFAULT_BATTERY_LEVEL = 100;

// Enrollment reader gateway ID
export const ENROLLMENT_READER_GATEWAY_ID = 'GW_TEST_ENROLMENT';

// Gateway threshold for test gateways
export const DEFAULT_GATEWAY_THRESHOLD = -70;

// Beacon signal variance (±3 dBm)
export const SIGNAL_VARIANCE = 6;

// iBeacon constants
export const IBEACON_UUID = 'E2C56DB5DFFB48D2B060D0F5A71096E0';
export const IBEACON_TX_POWER = -59;

// Test temperature/humidity sensors (C3 prefix is valid for MST01 sensors)
// MAC addresses must be 12 hex characters to match real sensor format
export type SensorModelType = 'FancyWhite' | 'MST01' | 'MST01V2';

export interface TestSensor {
  id: string;
  label: string;
  color: string;
  model: SensorModelType;
  defaultTemperature: number; // in Fahrenheit
  defaultHumidity: number; // percentage
  hasHumidity: boolean; // Some sensors (like deep freezers) don't have humidity probes
  firmwareVersion: string;
}

// Sensor type identifiers for rawData encoding
export const SENSOR_TYPE_IDS: Record<SensorModelType, string> = {
  FancyWhite: '0500',
  MST01: '0501',
  MST01V2: '0502',
};

export const TEST_SENSORS: TestSensor[] = [
  {
    id: 'C300000E5701', // Test sensor 01 - Med Fridge
    label: 'Med Fridge',
    color: '#00BCD4',
    model: 'MST01',
    defaultTemperature: 38,
    defaultHumidity: 45,
    hasHumidity: true,
    firmwareVersion: '2.1.0',
  },
  {
    id: 'C300000E5702', // Test sensor 02 - Blanket Warmer
    label: 'Blanket Warmer',
    color: '#FF5722',
    model: 'FancyWhite',
    defaultTemperature: 110,
    defaultHumidity: 15,
    hasHumidity: true,
    firmwareVersion: '1.5.3',
  },
  {
    id: 'C300000E5703', // Test sensor 03 - OR Room 1
    label: 'OR Room 1',
    color: '#4CAF50',
    model: 'MST01V2',
    defaultTemperature: 68,
    defaultHumidity: 50,
    hasHumidity: true,
    firmwareVersion: '3.0.1',
  },
  {
    id: 'C300000E5704', // Test sensor 04 - Deep Freezer (no humidity)
    label: 'Deep Freezer',
    color: '#9C27B0',
    model: 'MST01V2',
    defaultTemperature: -4, // Deep freezer temp in F
    defaultHumidity: 0, // No humidity probe
    hasHumidity: false,
    firmwareVersion: '3.0.1',
  },
];

// Sensor signal constants
export const DEFAULT_SENSOR_BATTERY = 95;
export const SENSOR_TEMP_VARIANCE = 0.5; // ±0.5°F variance per ping
export const SENSOR_HUMIDITY_VARIANCE = 1; // ±1% variance per ping
